import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SegmentNode, SegmentPreset } from '@mailflow/core/segments';
import { api } from './api.js';

export interface SegmentSummary {
  id: string;
  name: string;
  description: string | null;
  definition: SegmentNode;
  mode: string;
  memberCount: number | null;
  lastEvaluatedAt: string | null;
  evaluationMs: number | null;
  createdAt: string;
}

export interface SegmentEvaluation {
  total: number;
  totalContacts: number;
  durationMs: number;
  sample: Array<{
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    status: string;
  }>;
}

export const segmentsQuery = queryOptions({
  queryKey: ['segments'],
  queryFn: ({ signal }) => api.get<{ items: SegmentSummary[] }>('/api/segments', signal),
  staleTime: 15_000,
});

export const segmentQuery = (id: string) =>
  queryOptions({
    queryKey: ['segments', 'detail', id],
    queryFn: ({ signal }) => api.get<{ segment: SegmentSummary }>(`/api/segments/${id}`, signal),
  });

export const presetsQuery = queryOptions({
  queryKey: ['segments', 'presets'],
  queryFn: ({ signal }) => api.get<{ items: SegmentPreset[] }>('/api/segments/presets', signal),
  staleTime: Infinity,
});

/**
 * Live-Vorschau.
 *
 * Bewusst als Query mit dem Regelbaum im Schluessel: React Query
 * dedupliziert damit gleiche Baeume und haelt das letzte Ergebnis sichtbar,
 * waehrend die naechste Auswertung laeuft — die Zahl springt nicht auf null,
 * sobald der Nutzer ein Zeichen tippt.
 */
export const previewQuery = (definition: SegmentNode) =>
  queryOptions({
    queryKey: ['segments', 'preview', definition],
    queryFn: () =>
      api.post<SegmentEvaluation>('/api/segments/preview', { definition, sampleSize: 20 }),
    staleTime: 30_000,
    retry: false,
  });

export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string | null; definition: SegmentNode }) =>
      api.post<{ segment: SegmentSummary }>('/api/segments', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });
}

export function useUpdateSegment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; description?: string | null; definition?: SegmentNode }) =>
      api.put<{ segment: SegmentSummary }>(`/api/segments/${id}`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/segments/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });
}
